import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Input } from "shared/ui/input"
import { Button } from "shared/ui/button"
import { Label } from "shared/ui/label"
import { Alert, AlertDescription } from "shared/ui/alert"
import { ArrowLeft, Mail, CheckCircle, AlertTriangle } from "lucide-react"
import { requestContractorPasswordReset } from "modules/auth/api/authService"

export function ContractorForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)
    setError("")
    setMessage("")

    try {
      const response = await requestContractorPasswordReset({ email: email.trim() })
      setMessage(response.message || "ระบบได้ส่งลิงก์สำหรับรีเซ็ตรหัสผ่านไปยังอีเมลของคุณแล้ว")
    } catch (err: any) {
      console.error(err)
      const errorMessage = err?.response?.data ?? "ไม่สามารถส่งลิงก์รีเซ็ตรหัสผ่านได้"
      setError(typeof errorMessage === "string" ? errorMessage : "ไม่สามารถส่งลิงก์รีเซ็ตรหัสผ่านได้")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-6">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate("/login/contractor")}
          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 self-start"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          กลับไปหน้าเข้าสู่ระบบ
        </Button>

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur w-full">
          <CardHeader className="pb-4 text-center space-y-2">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-orange-900">
              <Mail className="w-6 h-6 text-orange-500" />
              ขอรีเซ็ตรหัสผ่านผู้รับเหมา
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error ? (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            ) : null}

            {message ? (
              <Alert className="border-emerald-200 bg-emerald-50">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700">{message}</AlertDescription>
              </Alert>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contractorEmail">Email</Label>
                <Input
                  id="contractorEmail"
                  type="email"
                  placeholder="example@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  autoComplete="email"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>กำลังส่งอีเมล...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>ส่งลิงก์รีเซ็ตรหัสผ่าน</span>
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

